import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
export function Hero() {
  return <section className="w-full min-h-screen flex flex-col items-center justify-center px-8 py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-id="element-136">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.015)_1px,transparent_1px)] bg-[size:80px_80px]" data-id="element-137" />
      <div className="max-w-7xl mx-auto w-full relative z-10" data-id="element-138">
        <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-139">
          {/* Left column - Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="space-y-10" data-id="element-140">
            {/* Logo */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6
          }} className="flex items-center gap-4" data-id="element-141">
              <div className="relative w-16 h-16 flex-shrink-0" data-id="element-142">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/njTUoX7DVWPKz91wtv3CVR/PCC_BGR2.png" alt="Pericles Capital" className="w-full h-full object-contain" data-id="element-143" />
              </div>
              <div data-id="element-144">
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-id="element-145">
                  Pericles Capital
                </h1>
                <p className="text-xs text-slate-600 font-semibold tracking-widest uppercase" data-id="element-146">
                  Quantitative Research Lab
                </p>
              </div>
            </motion.div>
            {/* Main headline */}
            <div className="space-y-6" data-id="element-147">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.1] tracking-tight" data-id="element-148">
                Markets Too Small
                <br data-id="element-149" />
                <span className="text-blue-600" data-id="element-150">For Big Funds</span>
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="text-xl text-slate-600 leading-relaxed max-w-2xl" data-id="element-151">
                An independent student-run quantitative research lab applying
                institutional-grade finance, mathematics, computer science, and
                data science to illiquid and overlooked markets.
              </motion.p>
            </div>
            {/* CTA Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="flex flex-wrap gap-4" data-id="element-152">
              <a href="https://forms.gle/ibuLiBnpwFqdeVNy7" target="_blank" rel="noopener noreferrer" className="group px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl" data-id="element-153">
                Apply to Join
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-154" />
              </a>
              <a href="#about" className="px-8 py-4 border-2 border-slate-900 text-slate-900 rounded-lg font-semibold hover:bg-slate-900 hover:text-white transition-all" data-id="element-155">
                Learn More
              </a>
            </motion.div>
            {/* Stats */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="grid grid-cols-3 gap-8 pt-8 border-t border-slate-200" data-id="element-156">
              <div data-id="element-157">
                <div className="text-3xl font-bold text-slate-900" data-id="element-158">2025</div>
                <div className="text-sm text-slate-600 font-medium uppercase tracking-wider" data-id="element-159">
                  Founded
                </div>
              </div>
              <div data-id="element-160">
                <div className="text-3xl font-bold text-slate-900" data-id="element-161">6+</div>
                <div className="text-sm text-slate-600 font-medium uppercase tracking-wider" data-id="element-162">
                  Markets
                </div>
              </div>
              <div data-id="element-163">
                <div className="text-3xl font-bold text-slate-900" data-id="element-164">100%</div>
                <div className="text-sm text-slate-600 font-medium uppercase tracking-wider" data-id="element-165">
                  Student-Run
                </div>
              </div>
            </motion.div>
          </motion.div>
          {/* Right column - Image */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative hidden lg:block" data-id="element-166">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-slate-100 shadow-2xl" data-id="element-167">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=800&fit=crop" alt="Professional team collaboration" className="w-full h-full object-cover" data-id="element-168" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" data-id="element-169" />
            </div>
            {/* Floating card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-2xl p-6 border border-slate-200" data-id="element-170">
              <div className="text-xs text-slate-600 font-semibold mb-2 uppercase tracking-wider" data-id="element-171">
                Research Focus
              </div>
              <div className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-172">
                Quantitative Finance
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}